import { useMemo, useRef, useState } from 'react';
import { Box, FlexBox, getTokenCssVar } from '@vp/swan';
import type { ReactNode } from 'react';
import { createChatApi } from '../../client/client';
import { useChat } from '../../hooks/useChat';
import { ChatCompletionCard } from '../ChatCompletionCard';
import { ChatCompletionLink } from '../ChatCompletionLink';
import { ChatErrorBanner } from '../ChatErrorBanner';
import { ChatInput } from '../ChatInput';
import { ChatLoadingSpinner } from '../ChatLoadingSpinner';
import { ChatMessageLimitCard } from '../ChatMessageLimitCard';
import { ChatStaleBanner } from '../ChatStaleBanner';
import { ChatStatusRegion } from '../ChatStatusRegion';
import { ChatThread } from '../ChatThread';
import { renderGenerativeUiSelector } from '../generative-ui';
import type { EsProduct, PrintProductRef } from '../../product/types';
import type { FileUploaderPort } from '../../upload';

export type ChatInterfaceProps = {
    briefId: string;
    baseUrl: string;
    authorizationHeader: string;
    /**
     * Injected error-reporting port for failures the library encounters while
     * normalizing chat history (for example, a generative-UI reply that fails to
     * parse). The library deliberately contains no reporting SDK of its own — the
     * host supplies one, such as wiring this to Bugsnag. Omitted, it defaults to a
     * no-op, so the library stays fully usable without a reporter.
     */
    onError?: (error: unknown, context: Record<string, unknown>) => void;
    /**
     * Called when a customer clicks a call-to-action link the assistant offered. Like
     * `onError`, this is an injected port: the library carries no analytics SDK, so the
     * host decides what a click means and reports it.
     */
    onCtaLinkClick?: (url: string, label: string) => void;
    /** Called when the customer clicks "Review and checkout" once the brief is complete. */
    onReview: () => void;
    /** Shown on the completion card, when supplied. */
    esProduct?: EsProduct;
    /**
     * Called when the customer clicks the "fill out a form instead" CTA below the composer.
     * The CTA is only visible when this callback is provided and the conversation has one or
     * fewer messages (initial state or one assistant reply). Omitted, the CTA is not shown.
     */
    onSwitchToForm?: () => void;
    uploader?: FileUploaderPort;
    /**
     * The print product this brief is for, supplied by the route. Required by the
     * print-configuration selector, which resolves its option list from the product; other
     * selectors ignore it.
     *
     * Optional because not every brief has one, and the route is the only place that knows.
     * Omitted, the print-configuration selector still renders and can be answered with "let my
     * designer decide" — it just cannot offer specific options.
     */
    printProduct?: PrintProductRef;
};

export function ChatInterface({
    briefId,
    baseUrl,
    authorizationHeader,
    onError,
    onCtaLinkClick,
    onReview,
    esProduct,
    onSwitchToForm,
    uploader,
    printProduct,
}: ChatInterfaceProps) {
    // Always the latest header, read lazily by each request. Kept out of `api`'s deps
    // below so a token refresh — which re-delivers this on every silent renewal via
    // useIdentityContext() — never rebuilds the client on its own. A genuine sign-in/
    // sign-out can't reach this hook as a live prop change either: it requires leaving
    // and returning to the page, which already remounts this component with a fresh
    // `baseUrl`-only dependency, so there's no live transition to react to here.
    const authorizationHeaderRef = useRef(authorizationHeader);
    authorizationHeaderRef.current = authorizationHeader;

    const api = useMemo(
        () =>
            createChatApi({
                baseUrl,
                getAuthorizationHeader: () => authorizationHeaderRef.current,
            }),
        [baseUrl]
    );

    const chat = useChat(briefId, api, onError);

    // Once the customer chooses to keep chatting past a completed brief, the composer
    // stays available for the rest of this mount.
    const [continued, setContinued] = useState(false);
    const canSwitchToForm = Boolean(onSwitchToForm) && chat.messages.length <= 1;

    const isBusy = chat.sendState === 'sending';
    // `conversation_full` is redirected to ChatMessageLimitCard instead of an error banner.
    const showError =
        chat.sendState === 'error' ||
        (chat.sendState === 'blocked' && chat.blockedReason !== 'conversation_full');
    // Keyed on the fields rather than the object, because the caller has no reason to memoise a
    // two-string literal and every reason to write it inline. Keyed on the object, a fresh
    // identity per host render would reach the print-configuration selector as a new query
    // variable and refetch its options on every unrelated keystroke.
    const mpvId = printProduct?.mpvId;
    const tenant = printProduct?.tenant;
    const generativeUiDeps = useMemo(
        () =>
            mpvId !== undefined && tenant !== undefined ? { printProduct: { mpvId, tenant } } : {},
        [mpvId, tenant]
    );

    const selector = chat.pendingComponent
        ? renderGenerativeUiSelector(
              chat.pendingComponent,
              chat.onSubmitComponentReply,
              generativeUiDeps
          )
        : null;
    // `!isBusy` so the card can't appear before the response that completed the brief resolves.
    const showCompletionCard = chat.isBriefCompleted && !isBusy && !continued;
    const showCompletionLink = chat.isBriefCompleted && continued;
    const showMessageLimitCard = chat.isMessageLimitReached && !isBusy;
    // Rendered inside the message log, below the last message, so the failure sits with the
    // turn that caused it rather than floating above the composer.
    let sendErrorBanner: ReactNode = null;
    if (showError) {
        sendErrorBanner = (
            <ChatErrorBanner
                errorCode={chat.errorCode}
                retriesExhausted={chat.retriesExhausted}
                onRetry={chat.onRetry}
                onReview={onReview}
            />
        );
    } else if (chat.sendState === 'stale') {
        sendErrorBanner = <ChatStaleBanner />;
    }

    return (
        <FlexBox flexDirection="column" style={{ height: '100%', minHeight: 0 }}>
            <ChatStatusRegion loading={chat.loading} sendState={chat.sendState} />
            {chat.loading ? (
                <ChatLoadingSpinner />
            ) : (
                <>
                    <ChatThread
                        messages={chat.messages}
                        error={chat.loadError}
                        errorCode={chat.loadErrorCode}
                        onReload={chat.reload}
                        reloadBudgetExhausted={chat.reloadBudgetExhausted}
                        awaitingReply={isBusy}
                        latestReplyKey={chat.latestReplyKey}
                        onCtaLinkClick={onCtaLinkClick}
                        sendErrorBanner={sendErrorBanner}
                    />
                    {(() => {
                        if (showCompletionCard) {
                            return (
                                <ComposerChrome>
                                    <ChatCompletionCard
                                        onReview={onReview}
                                        onContinue={() => setContinued(true)}
                                        canContinue={!chat.isMessageLimitReached}
                                        esProduct={esProduct}
                                    />
                                </ComposerChrome>
                            );
                        }

                        if (showMessageLimitCard) {
                            return (
                                <ComposerChrome>
                                    <ChatMessageLimitCard onReview={onReview} />
                                </ComposerChrome>
                            );
                        }

                        if (selector) {
                            return <ComposerChrome>{selector}</ComposerChrome>;
                        }

                        return (
                            <>
                                {showCompletionLink && <ChatCompletionLink onReview={onReview} />}
                                <ChatInput
                                    value={chat.inputText}
                                    onChange={chat.onInputChange}
                                    onSend={chat.onSend}
                                    onSwitchToForm={onSwitchToForm}
                                    canSwitchToForm={canSwitchToForm}
                                    isBusy={isBusy}
                                    isDisabled={
                                        chat.blockedReason !== null ||
                                        chat.retriesExhausted ||
                                        chat.loadError !== null
                                    }
                                    uploader={uploader}
                                    attachments={chat.attachments}
                                    onAttachmentsAdd={chat.onAttachmentsAdd}
                                    onAttachmentRemove={chat.onAttachmentRemove}
                                    onError={onError}
                                />
                            </>
                        );
                    })()}
                </>
            )}
        </FlexBox>
    );
}

/**
 * The composer slot's shared chrome — background band and centred column — for the
 * selector, completion, and message-limit states. `ChatInput` renders its own equivalent.
 */
function ComposerChrome({ children }: { children: ReactNode }) {
    return (
        <Box style={{ flexShrink: 0, backgroundColor: getTokenCssVar('SwanSemColorBgStrong') }}>
            <Box style={{ maxWidth: '768px', margin: '0 auto' }}>{children}</Box>
        </Box>
    );
}
